// =============================================================================
// magazine.tsx — premium "Tilla" variant (paired with the noir-gold layout).
//
// This is the showpiece for w04. Treatment notes:
//   - Foil-gold typography on names + key headers (gold gradient + clip + soft
//     drop-shadow simulating stamped foil).
//   - Magazine-cover hero with gold-bordered portrait, FEATURED ribbon, and
//     a cover-strip masthead.
//   - IntersectionObserver-driven scroll reveal for each section: opacity +
//     small upward shift + a subtle letter-spacing easing on titles. All
//     animation disabled when prefers-reduced-motion: reduce.
//   - Vertical "chapter" timeline — Roman numerals, gold rules, italic body.
//   - Dress-code showpiece: tactile color swatches, gold chip text, RSVP-bound
//     personal preferred-attire suggestion.
//   - Tasteful sound: chime (RSVP confirm), click (CTAs), swoosh (section
//     enter), tap (swatch hover).
// =============================================================================

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  GuestPhoto,
  CountdownSection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── Foil-gold text style ───────────────────────────────────────────────
// Linear gradient → background-clip text → soft drop-shadow. This is the
// closest CSS-only analogue to a foil-stamping effect.
function foilStyle(accent: string): CSSProperties {
  return {
    backgroundImage: `linear-gradient(100deg, ${accent} 0%, #f4d98a 22%, #fff7dc 38%, #b88638 55%, #f4d98a 72%, ${accent} 100%)`,
    backgroundSize: "200% 100%",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    color: "transparent",
    textShadow: "0 1px 0 rgba(201,162,78,0.18), 0 0 18px rgba(201,162,78,0.12)",
  };
}

// ─── Reveal wrapper ─────────────────────────────────────────────────────
// IntersectionObserver-based fade/lift. Plays a sparse "swoosh" on first
// entry per section. Reduced-motion users get an instant reveal and no sound.
function Reveal({
  children,
  onEnter,
  style,
  delay = 0,
}: {
  children: ReactNode;
  onEnter?: () => void;
  style?: CSSProperties;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Safe SSR/older-browser fallback.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired.current) {
            fired.current = true;
            setVisible(true);
            onEnter?.();
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [onEnter]);

  return (
    <div
      ref={ref}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 900ms cubic-bezier(.2,.7,.2,1) ${delay}ms, transform 900ms cubic-bezier(.2,.7,.2,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Foil-gold heading ──────────────────────────────────────────────────
// Wraps section titles with the foil treatment + a subtle letterspacing
// release on enter (handled via the `revealed` flag from the parent Reveal
// effect). The animation is also disabled for reduced motion via @media.
function FoilHeading({
  children,
  font,
  accent,
  size = 56,
  italic = true,
}: {
  children: ReactNode;
  font: string;
  accent: string;
  size?: number;
  italic?: boolean;
}) {
  return (
    <h2
      className="m-foil-heading"
      style={{
        ...foilStyle(accent),
        fontFamily: font,
        fontSize: size,
        fontStyle: italic ? "italic" : "normal",
        fontWeight: 400,
        margin: 0,
      }}
    >
      {children}
    </h2>
  );
}

// ─── Mini monogram crest (echoes the layout's signature element) ────────
function MiniCrest({ initials, color, size = 64 }: { initials: string; color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="m-foil" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="50%" stopColor="#f4d98a" />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <g stroke="url(#m-foil)" strokeWidth="0.6" fill="none">
        <circle cx="32" cy="32" r="28" />
        <circle cx="32" cy="32" r="24" opacity="0.4" />
        <path d="M14 32 Q 20 22 28 22 Q 32 22 32 18" />
        <path d="M50 32 Q 44 22 36 22 Q 32 22 32 18" />
        <path d="M14 32 Q 20 42 28 42 Q 32 42 32 46" />
        <path d="M50 32 Q 44 42 36 42 Q 32 42 32 46" />
      </g>
      <text
        x="32"
        y="38"
        textAnchor="middle"
        fontFamily="Italiana, serif"
        fontSize="18"
        fill="url(#m-foil)"
        letterSpacing="1"
      >
        {initials}
      </text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT — MAGAZINE (premium Tilla)
// ═══════════════════════════════════════════════════════════════════════
export function VariantMagazine({ t, isDark, ctx }: VariantProps) {
  const p = t.photos || [];
  const { play } = useSound();
  const tt = useT();
  const parts = t.names.split("&").map((s) => s.trim());
  const initials = `${(parts[0] || "")[0] || "T"}${(parts[1] || "")[0] || "L"}`.toUpperCase();
  const firstName = parts[0] || "siz";
  const swooshOnce = useRef(false);
  const swoosh = () => {
    // Throttle the swoosh so back-to-back reveals don't pile up audio.
    if (swooshOnce.current) return;
    swooshOnce.current = true;
    play("swoosh");
    window.setTimeout(() => {
      swooshOnce.current = false;
    }, 380);
  };

  return (
    <div
      className="tilla-variant"
      style={{ ["--tilla-accent" as string]: t.accent, ["--tilla-bg" as string]: t.bg, ["--tilla-ink" as string]: t.ink } as CSSProperties}
    >
      {/* Local CSS — keyframes + reduced-motion guard. */}
      <style>{`
        @keyframes m-foil-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes m-letter-release {
          0%   { letter-spacing: 0.18em; opacity: 0; }
          100% { letter-spacing: -0.005em; opacity: 1; }
        }
        @keyframes m-line-rise {
          0%   { transform: translateY(110%); }
          100% { transform: translateY(0); }
        }
        @keyframes m-rule-draw {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        .m-foil-heading {
          animation: m-foil-shimmer 8s linear infinite;
        }
        .m-cover-name {
          animation: m-foil-shimmer 8s linear infinite, m-letter-release 1100ms ease-out both;
        }
        .m-line-mask { display: inline-block; overflow: hidden; }
        .m-line-mask > span {
          display: inline-block;
          animation: m-line-rise 900ms cubic-bezier(.2,.7,.2,1) both;
        }
        .m-rule {
          transform-origin: left center;
          animation: m-rule-draw 1100ms cubic-bezier(.2,.7,.2,1) 200ms both;
        }
        .m-link {
          position: relative;
          display: inline-block;
        }
        .m-link::after {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: -2px;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 380ms cubic-bezier(.2,.7,.2,1);
        }
        .m-link:hover::after { transform: scaleX(1); }
        .m-swatch {
          transition: transform 280ms cubic-bezier(.2,.7,.2,1), box-shadow 280ms ease;
          will-change: transform;
        }
        .m-swatch:hover {
          transform: translateY(-4px) scale(1.06);
          box-shadow: 0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(201,162,78,0.5);
        }
        .m-cta {
          transition: transform 220ms cubic-bezier(.2,.7,.2,1), box-shadow 220ms ease, letter-spacing 220ms ease;
        }
        .m-cta:hover {
          transform: translateY(-1px);
          letter-spacing: 0.34em;
          box-shadow: 0 6px 18px rgba(201,162,78,0.25);
        }
        @media (prefers-reduced-motion: reduce) {
          .m-foil-heading,
          .m-cover-name,
          .m-line-mask > span,
          .m-rule,
          .m-swatch,
          .m-cta { animation: none !important; transition: none !important; }
          .m-cover-name { letter-spacing: -0.03em !important; opacity: 1 !important; }
        }

        /* ─── Mobile / phone overrides (≤768px) ───────────────────────── */
        .tilla-variant .mobile-only { display: none; }
        @media (max-width: 768px) {
          .tilla-variant .desktop-only { display: none !important; }
          .tilla-variant .mobile-only { display: block !important; }

          /* Limited-edition tag — premium status hint at the very top */
          .tilla-variant .m-edition-tag {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            padding: 10px 12px 6px;
            background: var(--tilla-bg, transparent);
          }
          .tilla-variant .m-edition-tag span {
            font-family: var(--font-ui);
            font-size: 9px;
            font-weight: 700;
            letter-spacing: 0.42em;
          }
          .tilla-variant .m-edition-tag .m-edition-rule {
            flex: 0 0 32px;
            height: 1px;
          }

          /* Cover masthead — stack vertically into a centered "deck" */
          .tilla-variant .m-masthead {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center;
            gap: 10px !important;
            padding-bottom: 16px !important;
            margin-bottom: 28px !important;
          }
          .tilla-variant .m-masthead > * {
            font-size: 9px !important;
            letter-spacing: 0.36em !important;
          }
          .tilla-variant .m-masthead .m-masthead-venue {
            font-size: 16px !important;
            letter-spacing: 0 !important;
          }

          /* Cover grid — stack image above headline, monogram crest centered */
          .tilla-variant .m-cover-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
            text-align: center;
          }
          .tilla-variant .m-cover-grid .m-cover-text {
            order: 2;
          }
          .tilla-variant .m-cover-grid .m-cover-photo {
            order: 1;
            border-width: 4px !important;
          }

          /* "The Wedding of" handwritten lead — smaller on mobile */
          .tilla-variant .m-cover-grid .m-line-mask {
            font-size: 24px !important;
            margin-bottom: 12px !important;
          }

          /* Foil-gold cover names — Vogue-cover proportions on a phone */
          .tilla-variant .m-cover-name {
            font-size: clamp(36px, 12vw, 72px) !important;
            line-height: 0.92 !important;
            letter-spacing: -0.025em !important;
            /* Tighter foil glow so the gradient reads as luxe at small sizes */
            text-shadow: 0 1px 0 rgba(201,162,78,0.25), 0 0 6px rgba(201,162,78,0.18) !important;
          }
          .tilla-variant .m-cover-grid .m-cover-photo {
            order: 1;
            aspect-ratio: auto !important;
            width: 100% !important;
            height: min(60vh, 560px) !important;
            max-width: none !important;
            margin: 0 !important;
          }
          .tilla-variant .m-cover-section {
            padding: 64px 18px 48px !important;
          }

          /* Sana / Vaqt / Joy — center, even spacing, no wrap chaos */
          .tilla-variant .m-cover-meta {
            justify-content: center !important;
            gap: 22px !important;
            margin-top: 20px !important;
          }
          .tilla-variant .m-cover-meta > div { text-align: center; }
          .tilla-variant .m-cover-meta > div > div:first-child { font-size: 9px !important; }
          .tilla-variant .m-cover-meta > div > div:last-child { font-size: 18px !important; }

          /* Cover section padding */
          .tilla-variant .m-cover-section {
            min-height: auto !important;
          }

          /* Mini crest beside headline → centered above names */
          .tilla-variant .m-cover-mini-crest {
            margin: 0 auto 12px;
            display: flex;
            justify-content: center;
          }

          /* Triptych → vertical reel (stacked, foil "SHOT" labels below each) */
          .tilla-variant .m-spread-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto !important;
            gap: 28px !important;
            min-height: 0 !important;
          }
          .tilla-variant .m-spread-grid > div:nth-child(2) {
            grid-template-rows: auto !important;
            display: grid !important;
            gap: 28px !important;
          }
          .tilla-variant .m-spread-grid .m-spread-tile {
            aspect-ratio: 4/5;
          }
          .tilla-variant .m-spread-section {
            padding: 56px 22px !important;
          }
          .tilla-variant .m-spread-header {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center;
            gap: 10px !important;
            margin-bottom: 28px !important;
          }
          .tilla-variant .m-spread-header > div { font-size: 9px !important; }
          .tilla-variant .m-spread-header h2 { font-size: 36px !important; }

          /* Pull quote — capped, generous padding, foil quotes still gold */
          .tilla-variant .m-quote-section {
            padding: 56px 22px !important;
          }
          .tilla-variant .m-quote-section .m-quote-text {
            font-size: 22px !important;
            line-height: 1.4 !important;
            max-width: 100% !important;
          }
          .tilla-variant .m-quote-section .m-quote-mark {
            font-size: 2.4em !important;
          }

          /* Roman-chapter timeline — stack year above event */
          .tilla-variant .m-chapter-section {
            padding: 64px 22px !important;
          }
          .tilla-variant .m-chapter-section .m-chapter-rule {
            display: none;
          }
          .tilla-variant .m-chapter-section .m-chapter-row {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto auto !important;
            gap: 8px !important;
            padding: 18px 0 !important;
            text-align: center;
            border-top: 1px solid rgba(201,162,78,0.35);
          }
          .tilla-variant .m-chapter-section .m-chapter-row > div:nth-child(2) {
            display: none; /* hide the rule-bullet, divider replaces it */
          }
          .tilla-variant .m-chapter-section .m-chapter-num {
            font-size: 36px !important;
          }
          .tilla-variant .m-chapter-section .m-chapter-event {
            font-size: 24px !important;
          }
          .tilla-variant .m-chapter-section .m-chapter-sub {
            font-size: 15px !important;
          }
          .tilla-variant .m-chapter-section h2 { font-size: 38px !important; }

          /* Dress code — 2x2 swatch grid + full-width foil RSVP strip */
          .tilla-variant .m-dress-section {
            padding: 56px 22px !important;
          }
          .tilla-variant .m-dress-section h2 { font-size: 32px !important; }
          .tilla-variant .m-dress-section .m-dress-blurb {
            font-size: 15px !important;
            margin-top: 14px !important;
          }
          .tilla-variant .m-dress-section .m-dress-rsvp-chip {
            display: block !important;
            width: 100%;
            margin: 22px 0 0 !important;
            padding: 12px 14px !important;
            font-size: 10px !important;
            letter-spacing: 0.22em !important;
            background: linear-gradient(100deg, var(--tilla-accent) 0%, #f4d98a 50%, var(--tilla-accent) 100%);
            color: var(--tilla-ink) !important;
            -webkit-text-fill-color: var(--tilla-ink) !important;
            -webkit-background-clip: border-box !important;
            background-clip: border-box !important;
            text-shadow: none !important;
          }
          .tilla-variant .m-dress-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 20px 16px !important;
            margin-top: 32px !important;
          }
          .tilla-variant .m-dress-grid .m-swatch > div:first-child {
            width: 64px !important;
            height: 64px !important;
            margin-bottom: 10px !important;
          }
          .tilla-variant .m-dress-grid .m-swatch > div:nth-child(2) { font-size: 10px !important; }
          .tilla-variant .m-dress-grid .m-swatch > div:nth-child(3) { font-size: 12px !important; }

          /* Tighten foil glow universally on mobile so gradients stay crisp */
          .tilla-variant .m-foil-heading {
            text-shadow: 0 1px 0 rgba(201,162,78,0.22), 0 0 6px rgba(201,162,78,0.16) !important;
          }
        }
      `}</style>

      {/* ─── MAGAZINE COVER ──────────────────────────────────────────── */}
      <Reveal onEnter={swoosh}>
        <section
          className="m-cover-section"
          style={{
            minHeight: "100vh",
            padding: "60px clamp(24px, 6vw, 80px)",
            position: "relative",
            overflow: "hidden",
            background: t.bg,
          }}
        >
          {/* Mobile-only — limited-edition status tag (premium tier signal) */}
          <div
            className="mobile-only m-edition-tag"
            style={{ display: "none" }}
            aria-hidden="true"
          >
            <span
              className="m-edition-rule"
              style={{ background: `linear-gradient(90deg, transparent, ${t.accent})` }}
            />
            <span
              className="m-foil-heading"
              style={{ ...foilStyle(t.accent) }}
            >
              {tt("variant.w04.cover.edition")}
            </span>
            <span
              className="m-edition-rule"
              style={{ background: `linear-gradient(90deg, ${t.accent}, transparent)` }}
            />
          </div>

          {/* Masthead strip */}
          <div
            className="m-masthead"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              borderBottom: `1px solid ${t.accent}`,
              paddingBottom: 20,
              marginBottom: 48,
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.32em",
                color: t.accent,
              }}
            >
              {tt("variant.w04.cover.vol")}
            </div>
            <div className="m-masthead-venue" style={{ fontFamily: t.font, fontStyle: "italic", fontSize: 22, color: t.ink }}>
              {t.venue}, {t.date.split(".")[2]}
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.32em",
                ...foilStyle(t.accent),
              }}
              className="m-foil-heading"
            >
              {tt("variant.w04.cover.exclusive")}
            </div>
          </div>

          <div
            className="m-cover-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
              gap: "clamp(24px, 5vw, 60px)",
              alignItems: "center",
            }}
          >
            <div className="m-cover-text">
              {/* Mobile-only mini crest above names — keep Tilla signature visible */}
              <div className="mobile-only m-cover-mini-crest" style={{ display: "none" }}>
                <MiniCrest initials={initials} color={t.accent} size={48} />
              </div>
              <div
                className="m-line-mask"
                style={{ fontFamily: "var(--font-hand)", fontSize: 36, color: t.accent, marginBottom: 16 }}
              >
                <span>{tt("variant.w04.cover.lead")}</span>
              </div>
              <h1
                className="m-cover-name"
                style={{
                  ...foilStyle(t.accent),
                  fontFamily: t.font,
                  fontSize: "clamp(56px, 10vw, 140px)",
                  lineHeight: 0.92,
                  fontWeight: 400,
                  letterSpacing: "-0.03em",
                  margin: 0,
                }}
              >
                {(parts[0] || t.names).toUpperCase()}
                <br />
                <span style={{ color: t.accent, fontStyle: "italic", WebkitTextFillColor: t.accent }}>&amp;</span>
                <br />
                {(parts[1] || "").toUpperCase()}
              </h1>

              {/* Drawing rule — animates on first reveal. */}
              <div
                className="m-rule"
                style={{
                  height: 1,
                  background: `linear-gradient(90deg, ${t.accent}, ${t.accent}33)`,
                  marginTop: 32,
                }}
              />

              <div
                className="m-cover-meta"
                style={{
                  display: "flex",
                  gap: 40,
                  marginTop: 24,
                  paddingTop: 16,
                  flexWrap: "wrap",
                }}
              >
                {([
                  [tt("variant.w04.cover.meta_date"), t.date],
                  [tt("variant.w04.cover.meta_time"), "17:00"],
                  [tt("variant.w04.cover.meta_venue"), t.venue],
                ] as const).map(([k, v]) => (
                  <div key={k}>
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.3em",
                        color: t.accent,
                        marginBottom: 6,
                      }}
                    >
                      {k}
                    </div>
                    <div style={{ fontFamily: t.font, fontSize: 24, fontStyle: "italic", color: t.ink }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Featured portrait — gold border + ribbon. */}
            <div
              className="m-cover-photo"
              style={{
                aspectRatio: "3/4",
                border: `8px solid ${t.accent}`,
                position: "relative",
                boxShadow: "0 30px 60px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,247,220,0.18)",
              }}
            >
              <GuestPhoto
                src={p[0]}
                style={{ position: "absolute", inset: 0, filter: "saturate(0.92) contrast(1.05)" }}
                fallbackBg={t.bg}
                fallbackTone={t.accent}
              />
              {/* duotone ink wash for the cover photo */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, transparent 40%, ${t.ink}aa 100%)`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: -1,
                  left: -1,
                  padding: "8px 16px",
                  background: t.accent,
                  color: t.bg,
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                }}
              >
                {tt("variant.w04.cover.featured")}
              </div>
              {/* Mini crest overlay */}
              <div style={{ position: "absolute", right: 14, bottom: 14 }}>
                <MiniCrest initials={initials} color={t.accent} size={56} />
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      {/* ─── TRIPTYCH FEATURE SPREAD ────────────────────────────────── */}
      {(p[1] || p[2] || p[3]) && (
        <Reveal onEnter={swoosh}>
          <section
            className="m-spread-section"
            style={{
              padding: "100px clamp(24px, 6vw, 60px)",
              borderTop: `1px solid ${t.accent}`,
              background: t.bg,
            }}
          >
            <div
              className="m-spread-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 40,
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.32em",
                  color: t.accent,
                }}
              >
                {tt("variant.w04.spread.feature_no")}
              </div>
              <FoilHeading font={t.font} accent={t.accent} size={56}>
                {tt("variant.w04.spread.title")}
              </FoilHeading>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.32em",
                  color: t.accent,
                }}
              >
                {tt("variant.w04.spread.pages")}
              </div>
            </div>
            <div
              className="m-spread-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
                gap: 24,
                minHeight: 560,
              }}
            >
              <div className="m-spread-tile" style={{ position: "relative", overflow: "hidden", border: `1px solid ${t.accent}55` }}>
                <GuestPhoto src={p[1] || p[0]} style={{ position: "absolute", inset: 0 }} fallbackBg={t.bg} fallbackTone={t.accent} />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 60%, ${t.ink}99)`,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 24,
                    bottom: 24,
                    padding: "6px 12px",
                    background: t.bg,
                    fontFamily: "var(--font-ui)",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    color: t.accent,
                  }}
                >
                  {tt("variant.w04.spread.shot1")}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 24 }}>
                {[2, 3].map((idx) => (
                  <div key={idx} className="m-spread-tile" style={{ position: "relative", overflow: "hidden", border: `1px solid ${t.accent}55` }}>
                    <GuestPhoto
                      src={p[idx] || p[0]}
                      style={{ position: "absolute", inset: 0 }}
                      fallbackBg={t.bg}
                      fallbackTone={t.accent}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(180deg, transparent 55%, ${t.ink}99)`,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 16,
                        bottom: 16,
                        padding: "6px 12px",
                        background: t.bg,
                        fontFamily: "var(--font-ui)",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.2em",
                        color: t.accent,
                      }}
                    >
                      SHOT 0{idx}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>
      )}

      {/* ─── PULL QUOTE ─────────────────────────────────────────────── */}
      <Reveal onEnter={swoosh}>
        <section
          className="m-quote-section"
          style={{
            padding: "100px clamp(24px, 6vw, 80px)",
            borderTop: `1px solid ${t.accent}`,
            borderBottom: `1px solid ${t.accent}`,
            textAlign: "center",
            background: `${t.accent}12`,
            position: "relative",
          }}
        >
          {/* Suzani ornament watermark */}
          <div style={{ position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)", opacity: 0.4 }}>
            <O.Suzani size={36} color={t.accent} />
          </div>
          <div
            className="m-quote-text"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(32px, 4vw, 56px)",
              fontStyle: "italic",
              fontWeight: 400,
              maxWidth: 900,
              margin: "0 auto",
              lineHeight: 1.3,
              color: t.ink,
            }}
          >
            <span className="m-quote-mark" style={{ ...foilStyle(t.accent), fontSize: "4em", lineHeight: 0, verticalAlign: "-0.4em" }}>&ldquo;</span>
            {tt("variant.w04.quote.text")}
            <span className="m-quote-mark" style={{ ...foilStyle(t.accent), fontSize: "4em", lineHeight: 0, verticalAlign: "-0.4em" }}>&rdquo;</span>
          </div>
        </section>
      </Reveal>

      <Reveal onEnter={swoosh}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ─── CHAPTER TIMELINE ───────────────────────────────────────── */}
      <Reveal onEnter={swoosh}>
        <section className="m-chapter-section" style={{ padding: "120px clamp(24px, 6vw, 60px)", borderTop: `1px solid ${t.ink}22`, position: "relative" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: t.accent,
                marginBottom: 16,
              }}
            >
              {tt("variant.w04.timeline.eyebrow")}
            </div>
            <FoilHeading font={t.font} accent={t.accent} size={56}>
              {tt("variant.w04.timeline.title")}
            </FoilHeading>
          </div>

          {/* Vertical "magazine" timeline — chapter numerals, gold rule, italic body. */}
          <div style={{ maxWidth: 880, margin: "0 auto", position: "relative" }}>
            {/* Gold center rule */}
            <div
              aria-hidden="true"
              className="m-chapter-rule"
              style={{
                position: "absolute",
                left: 110,
                top: 12,
                bottom: 12,
                width: 1,
                background: `linear-gradient(180deg, transparent, ${t.accent}, transparent)`,
              }}
            />
            {([
              ["I", "2019", tt("variant.w04.timeline.0.event"), tt("variant.w04.timeline.0.sub")],
              ["II", "2021", tt("variant.w04.timeline.1.event"), tt("variant.w04.timeline.1.sub")],
              ["III", "2024", tt("variant.w04.timeline.2.event"), tt("variant.w04.timeline.2.sub")],
              ["IV", "2026", tt("variant.w04.timeline.3.event"), tt("variant.w04.timeline.3.sub")],
            ] as const).map(([numeral, year, ev, sub], i) => (
              <Reveal key={i} delay={i * 80}>
                <div
                  className="m-chapter-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "100px 1px 1fr",
                    gap: 32,
                    padding: "36px 0",
                    alignItems: "baseline",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: t.font,
                        fontStyle: "italic",
                        fontSize: 28,
                        ...foilStyle(t.accent),
                      }}
                      className="m-foil-heading m-chapter-num"
                    >
                      {numeral}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.32em",
                        color: `${t.ink}99`,
                        marginTop: 6,
                      }}
                    >
                      {tt("variant.w04.timeline.chapter")} &middot; {year}
                    </div>
                  </div>
                  {/* Bullet on the rule */}
                  <div
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: t.accent,
                      transform: "translate(-4px, 6px)",
                      boxShadow: `0 0 0 4px ${t.bg}, 0 0 0 5px ${t.accent}55`,
                    }}
                  />
                  <div>
                    <div
                      className="m-chapter-event"
                      style={{
                        fontFamily: t.font,
                        fontSize: 32,
                        fontStyle: "italic",
                        marginBottom: 8,
                        color: t.ink,
                      }}
                    >
                      {ev}
                    </div>
                    <div
                      className="m-chapter-sub"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 17,
                        color: `${t.ink}cc`,
                        fontStyle: "italic",
                        lineHeight: 1.6,
                      }}
                    >
                      {sub}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal onEnter={swoosh}>
        <GallerySection t={t} />
      </Reveal>
      <Reveal onEnter={swoosh}>
        <ScheduleSection
          t={t}
          items={[
            [tt("variant.w04.schedule.0.time"), tt("variant.w04.schedule.0.event")],
            [tt("variant.w04.schedule.1.time"), tt("variant.w04.schedule.1.event")],
            [tt("variant.w04.schedule.2.time"), tt("variant.w04.schedule.2.event")],
            [tt("variant.w04.schedule.3.time"), tt("variant.w04.schedule.3.event")],
            [tt("variant.w04.schedule.4.time"), tt("variant.w04.schedule.4.event")],
          ]}
        />
      </Reveal>

      {/* ─── DRESS CODE — premium showpiece ─────────────────────────── */}
      <Reveal onEnter={swoosh}>
        <section
          className="m-dress-section"
          style={{
            padding: "120px clamp(24px, 6vw, 60px)",
            borderTop: `1px solid ${t.ink}22`,
            textAlign: "center",
            background: isDark
              ? `radial-gradient(ellipse at center, ${t.accent}18, ${t.ink}55 70%)`
              : `radial-gradient(ellipse at center, ${t.accent}22, ${t.accent}08 70%)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* corner ornaments */}
          <div style={{ position: "absolute", top: 24, left: 24, opacity: 0.55 }}>
            <O.DecoCorner size={36} color={t.accent} />
          </div>
          <div
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              opacity: 0.55,
              transform: "scaleX(-1)",
            }}
          >
            <O.DecoCorner size={36} color={t.accent} />
          </div>

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 16,
            }}
          >
            {tt("variant.w04.dress.eyebrow")}
          </div>
          <FoilHeading font={t.font} accent={t.accent} size={48}>
            {tt("variant.w04.dress.title")}
          </FoilHeading>

          <div
            className="m-dress-blurb"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 18,
              color: `${t.ink}cc`,
              maxWidth: 560,
              margin: "20px auto 0",
              lineHeight: 1.55,
            }}
          >
            {tt("variant.w04.dress.blurb")}
          </div>

          {/* Personalised note bound to RSVP guest name. */}
          {ctx.rsvp.guestName && (
            <div
              style={{
                marginTop: 28,
                display: "inline-block",
                padding: "10px 22px",
                border: `1px solid ${t.accent}`,
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.28em",
                ...foilStyle(t.accent),
              }}
              className="m-foil-heading m-dress-rsvp-chip"
            >
              {tt("variant.w04.dress.preferred_for").replace("{name}", ctx.rsvp.guestName.toUpperCase())}
            </div>
          )}

          <div
            className="m-dress-grid"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 36,
              flexWrap: "wrap",
              marginTop: 56,
            }}
          >
            {([
              ["#1A1814", tt("variant.w04.dress.swatch.0.name"), tt("variant.w04.dress.swatch.0.role")],
              ["#C9A24E", tt("variant.w04.dress.swatch.1.name"), tt("variant.w04.dress.swatch.1.role")],
              ["#F5EFE6", tt("variant.w04.dress.swatch.2.name"), tt("variant.w04.dress.swatch.2.role")],
              ["#6B1F2A", tt("variant.w04.dress.swatch.3.name"), tt("variant.w04.dress.swatch.3.role")],
            ] as const).map(([c, n, role]) => (
              <div
                key={n}
                className="m-swatch"
                role="img"
                aria-label={`${n} — ${role}`}
                style={{
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 78,
                    height: 78,
                    borderRadius: "50%",
                    background: c,
                    border: `2px solid ${t.accent}`,
                    boxShadow: `inset 0 0 0 4px ${t.bg}, 0 4px 14px rgba(0,0,0,0.25)`,
                    margin: "0 auto 14px",
                  }}
                />
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.22em",
                    ...foilStyle(t.accent),
                  }}
                  className="m-foil-heading"
                >
                  {n.toUpperCase()}
                </div>
                <div
                  style={{
                    fontFamily: t.font,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: `${t.ink}aa`,
                    marginTop: 4,
                  }}
                >
                  {role}
                </div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal onEnter={swoosh}>
        <MapSection t={t} isDark={isDark} addressLine={`${t.venue} · ${tt("variant.w04.map.address")}`} />
      </Reveal>

      {/* RSVP wrapper — wires chime to confirm + click to other interactions. */}
      <Reveal onEnter={swoosh}>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const btn = target.closest("button");
            if (!btn) return;
            const label = (btn.textContent || "").trim().toLowerCase();
            // "kelaman" (I'm coming) — RSVP yes confirmation → chime.
            // Catches any positive confirmation in Uzbek/EN.
            if (
              label.includes("kelaman") ||
              label.includes("tasdiq") ||
              label === "ha" ||
              label.includes("yes") ||
              label.includes("confirm")
            ) {
              play("chime");
            } else {
              play("click");
            }
          }}
        >
          <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} />
        </div>
      </Reveal>

      <Reveal onEnter={swoosh}>
        <GuestbookSection t={t} isDark={isDark} {...ctx.guestbook} />
      </Reveal>
      <Reveal onEnter={swoosh}>
        <ShareSection t={t} />
      </Reveal>

      {/* Signature footer — mini crest + foil signature line for first guest. */}
      <Reveal>
        <div style={{ textAlign: "center", padding: "36px 24px 0", color: t.ink }}>
          <MiniCrest initials={initials} color={t.accent} size={48} />
          <div
            style={{
              marginTop: 8,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 16,
              ...foilStyle(t.accent),
            }}
            className="m-foil-heading"
          >
            {tt("variant.w04.signoff").replace("{first}", firstName).replace("{second}", parts[1] || "")}
          </div>
        </div>
      </Reveal>
      <GuestFooter t={t} />
    </div>
  );
}
